import type { ReactNode, RefObject } from "preact/compat";
import {
	createContext,
	useContext,
	useMemo,
	useEffect,
	useRef,
} from "preact/compat";
import type {
	QueryHookOptions,
	QueryResult,
	MutationHookOptions,
	MutationTuple,
} from "@apollo/client/react";
import { useQuery, useMutation } from "@apollo/client/react";
import type {
	OperationVariables,
	DocumentNode,
	DefaultContext,
} from "@apollo/client/core";
import {
	ApolloClient,
	InMemoryCache,
	createHttpLink,
} from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import type { AccessToken } from "@brickme/frontend-common/src/auth/auth-user.ts";
import timeoutPromise from "~/utils/timeout-promise.ts";
import type { AuthenticationResult } from "./auth-state.tsx";
import {
	isLoadingAuthenticationResult,
	isSignedInAuthenticationResult,
	useAuthenticationState,
} from "./auth-state.tsx";
import type { SavedDesignsQuery } from "~/api-query/saved-designs-query.generated.ts";

const ApiGraphQlContext = createContext<ApolloClient<object> | undefined>(
	undefined,
);

type ApiGraphQlProviderProps = {
	readonly uri: string;
	readonly children: ReactNode;
};

async function waitForValidAccessToken(
	ref: RefObject<AuthenticationResult | undefined>,
): Promise<AccessToken | undefined> {
	const state = ref.current;
	if (!state) {
		return undefined;
	}

	if (isLoadingAuthenticationResult(state)) {
		await timeoutPromise(500);
		return waitForValidAccessToken(ref);
	}

	if (!isSignedInAuthenticationResult(state)) {
		return undefined;
	}

	return state.ensureValidAccessToken();
}

function ApiGraphQlProvider({ uri, children }: ApiGraphQlProviderProps) {
	const authState = useAuthenticationState();
	const authStateRef = useRef<AuthenticationResult>(authState);
	authStateRef.current = authState;

	const apiClient = useMemo(() => {
		const httpLink = createHttpLink({ uri });
		const authLink = setContext(async (_, { headers }) => {
			const accessToken = await waitForValidAccessToken(authStateRef);
			if (accessToken === undefined) {
				return { headers };
			}

			return {
				headers: {
					...headers,
					Authorization: accessToken.accessToken,
				},
			};
		});
		return new ApolloClient({
			link: authLink.concat(httpLink),
			cache: new InMemoryCache({
				typePolicies: {
					User: {
						fields: {
							savedDesigns: {
								// Don't cache separate results based on any of this field's arguments.
								keyArgs: false,
								// Concatenate the incoming list items with the existing list items.
								merge(
									existing:
										| undefined
										| SavedDesignsQuery["viewer"]["savedDesigns"],
									incoming: SavedDesignsQuery["viewer"]["savedDesigns"],
								): SavedDesignsQuery["viewer"]["savedDesigns"] {
									if (!existing) {
										return incoming;
									}
									return {
										...incoming,
										items: [...existing.items, ...incoming.items],
									};
								},
							},
						},
					},
				},
			}),
		});
	}, [uri]);

	// I think this actually happens one frame after the memo change, which could result in an error
	useEffect(
		() => () => {
			apiClient.stop();
			apiClient.resetStore();
		},
		[apiClient],
	);

	return (
		<ApiGraphQlContext.Provider value={apiClient}>
			{children}
		</ApiGraphQlContext.Provider>
	);
}

function useApiApolloClient(): ApolloClient<object> {
	const client = useContext(ApiGraphQlContext);
	if (!client) {
		throw new Error("No api graphql context");
	}
	return client;
}

function useApiQuery<TData = any, TVariables = OperationVariables>(
	query: DocumentNode,
	options?: Omit<QueryHookOptions<TData, TVariables>, "client">,
): QueryResult<TData, TVariables> {
	const client = useApiApolloClient();
	return useQuery<TData, TVariables>(query, {
		...options,
		// This makes sure loading is updated when fetchMore
		notifyOnNetworkStatusChange: true,
		client,
	});
}

function useApiMutation<
	TData = any,
	TVariables = OperationVariables,
	TContext = DefaultContext,
>(
	mutation: DocumentNode,
	options?: Omit<MutationHookOptions<TData, TVariables, TContext>, "client">,
): MutationTuple<TData, TVariables, TContext> {
	const client = useApiApolloClient();
	return useMutation<TData, TVariables, TContext>(mutation, {
		...options,
		client,
	});
}

export { useApiQuery, useApiApolloClient, ApiGraphQlProvider, useApiMutation };
